import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { applyBrand } from './app/brand'
// The brand presets' custom properties. HERE, not in carbon.scss: the three guest
// surfaces below load no Carbon, and a preset has to reach them too.
import './styles/presets.scss'
// The Refined design option. Matches nothing unless <html data-ui="refined">.
import './styles/refined.scss'
// Only the PREDICATES are static. Each is a few lines of string matching with
// no imports of its own, so all three cost nothing; the applications behind
// them are loaded one at a time, below.
import { isKioskURL } from './features/kiosk/entry'
import { isBookingURL } from './features/publicbooking/entry'
import { isRoomOrderURL } from './features/roomorder/entry'
import { deviceHome } from './app/deviceHome'

// The guest surfaces' client. The STAFF app builds its own, with the session
// handlers, in api/queryClient.ts.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The dominant real pattern is an agent switching back to a tab; focus
      // refetch makes that instant (docs/08 §6.1).
      refetchOnWindowFocus: true,
      retry: 2,
      staleTime: 5_000,
    },
    // Mutations are NEVER retried automatically: a retried booking without a
    // reused idempotency key is a second booking (docs/04 §6).
    mutations: { retry: 0 },
  },
})

// Before the first render: the accents are CSS custom properties, and a
// component that paints before they exist flashes the default blue.
applyBrand()

const root = document.getElementById('root')
if (!root) throw new Error('#root not found')

// THREE GUEST SURFACES SHIP IN THIS BUNDLE AND NONE OF THEM IS THE STAFF APP.
//
// `/kiosk` (docs/14), `/book` (docs/18) and `/order` (M15 W3) each branch here — above `AuthGate` and above `AppShell` — rather
// than being a tab inside the shell.
//
// The reason is not tidiness. `AppShell` paints the top bar, the two rails, the
// tab strip and the command palette; every one of those is a door from a guest
// surface into the front desk's screens. A guest app that renders the shell and
// hides the chrome with CSS is one stylesheet away from a guest reaching the
// tape chart. Branching before either is imported means the guest surface holds
// no reference to them at all.
//
// They key on the PATH, not on `?tab=`. `AppShell` owns `?tab=`, and a path is
// what a kiosk browser's "home page" setting takes, which is how the device is
// actually configured. The Go binary's SPA handler serves index.html for
// unknown paths, so none of the three needs a server route.
//
// There is no `/in-room`: the guest tablet is the native iPad app (docs/16),
// and the browser tablet that branched here was removed on 2026-10-28.
//
// `?mock=1` runs each against its simulator; the default, with no query, is the
// real API.
// EACH SURFACE IS LOADED ON ITS OWN, and a visitor pays for exactly the one
// they asked for.
//
// A guest on the booking page has no use for the kiosk's card-reader state
// machine, and a phone on a Lembongan connection
// should not download them. Static imports here put all three in the entry
// chunk; a dynamic import per branch puts each in its own.
function guestSurface(pathname: string): (() => Promise<React.ReactElement>) | null {
  if (isKioskURL(pathname)) {
    return async () => {
      const { KioskApp } = await import('./features/kiosk/KioskApp')
      return <KioskApp />
    }
  }
  if (isBookingURL(pathname)) {
    return async () => {
      const { BookingApp } = await import('./features/publicbooking/BookingApp')
      return <BookingApp />
    }
  }
  // `/order/<token>` — the QR card on a bedside table (M15 W3).
  //
  // THE THIRD GUEST SURFACE, and the only one with no session of any kind. The
  // kiosk signs in as a device account; the booking page has
  // no account but also nothing to steal. This one holds a bearer token in its
  // URL that can put charges on a room's bill, which is exactly why it branches
  // HERE — above `AuthGate` and above `AppShell`, so it holds no reference to
  // the staff screens at all rather than hiding them with CSS.
  //
  // Dynamic, like the other two. It imports no Carbon and no i18n catalogue,
  // so what a guest downloads is this page and React: a phone in a concrete
  // building on a hotel's guest wifi is the worst network in the product.
  if (isRoomOrderURL(pathname)) {
    return async () => {
      const { RoomOrderApp } = await import('./features/roomorder/RoomOrderApp')
      return <RoomOrderApp />
    }
  }
  return null
}

// A DEVICE ACCOUNT NEVER RENDERS THE STAFF SHELL, whatever URL it was given.
//
// The path branch above is not enough on its own. A kiosk signing in at `/` —
// which is what happens the first time somebody sets one up, and every time the
// browser's home page is left at the default — resolves a session and falls
// straight through to `AppShell`. The shell then shows a tab strip with nothing
// in it and fires the Tasks screen's queries, which come back 403 because the
// account holds exactly one capability. Harmless, and it looks exactly like a
// broken installation to the person holding the device.
//
// So the SECOND test is on the principal rather than the path (app/deviceHome):
// a kiosk account is sent to `/kiosk`, and the iPad's `inroom.operate` account,
// which has no surface in this bundle, gets a screen saying what it is for and
// Sign out. `/book` and `/order` have no counterpart: nobody signs in to either.
const guest = guestSurface(window.location.pathname)
const app = ReactDOM.createRoot(root)

// THE STAFF SHELL AND CARBON ARE LOADED ONLY WHEN THEY ARE NEEDED, and that is
// a REQUIREMENT rather than an optimisation.
//
// `docs/11` §M8 makes mobile Lighthouse ≥ 90 an exit criterion for the public
// booking page. Carbon is a 178 kB chunk plus 451 kB of CSS, and a static
// `import './styles/carbon.scss'` at module scope put a `modulepreload` for it
// in index.html — so a guest on a phone downloaded the entire staff design
// system before seeing a room. Measured: ~1.47 MB raw / ~359 kB gzip, which
// does not reach 90 on any connection Nusa Lembongan has.
//
// The guest surfaces import no Carbon at all (`publicbooking` is plain semantic
// HTML over the brand's CSS custom properties, and two tests enforce that), so
// the only thing that pulled it in was this file. Deferring both imports behind
// the branch is the whole fix.
//
// The staff app pays one extra round trip on first load, on a LAN, once per
// deploy. That is the right side of the trade.
if (guest) {
  void (async () => {
    const surface = await guest()
    app.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>{surface}</QueryClientProvider>
      </React.StrictMode>,
    )
  })()
} else {
  void (async () => {
    // `./i18n` is here for the same reason Carbon is: it is 155 kB of staff
    // catalogues across eight files, and NO guest surface reads a word of it.
    // The kiosk and the booking page all carry bilingual label
    // PAIRS in their own `labels.ts`, because a guest-facing artefact does not
    // consult the staff UI locale (A-086, docs/14 §5) — so importing i18next
    // and every namespace at module scope put the whole staff vocabulary in
    // front of a guest's first paint.
    //
    // Its import has a SIDE EFFECT (`i18n.init()`), so it is awaited before the
    // shell renders rather than merely requested.
    await Promise.all([import('./styles/carbon.scss'), import('./i18n')])
    // The staff client, the activity tracker and the device-account screen come
    // out of the AuthGate chunk rather than being imported on their own: every
    // dynamic import here is a surface root in scripts/check-js-budget.cjs, and
    // all three belong to the staff surface.
    const [{ AppShell }, { AuthGate, DeviceAppOnlyScreen, createStaffQueryClient, installActivityTracker }] =
      await Promise.all([
        import('./app/AppShell'),
        import('./auth/AuthGate'),
      ])
    // Staff only: from here on a GET that did not follow input is marked as a
    // background poll, so an unattended screen idle-locks (US-102).
    installActivityTracker()
    app.render(
      <React.StrictMode>
        <QueryClientProvider client={createStaffQueryClient()}>
          <AuthGate>
            {(user) => {
              // A device account signing in at `/` is REDIRECTED rather than
              // rendered in place, because its application now sits behind a
              // dynamic import and the shell has already been fetched by the
              // time we know who signed in. One navigation reads more clearly
              // than a second async branch here, and it puts the device on the
              // URL its browser should have been pointed at — which is the
              // actual configuration mistake.
              const home = deviceHome(user.capabilities)
              if (home?.kind === 'app-only') return <DeviceAppOnlyScreen />
              if (home && window.location.pathname !== home.path) {
                window.location.replace(home.path)
                return null
              }
              return <AppShell user={user} />
            }}
          </AuthGate>
        </QueryClientProvider>
      </React.StrictMode>,
    )
  })()
}
